import { useState, useEffect, useCallback, useRef } from "react";
import {
    calculateGexByDte,
    type GexLevels,
    type OptionRow,
} from "../../../../services/gexCalculator";
import { fetchGexChainRows } from "../../../../services/gexChainDataService";
import { getUnderlyingPriceFallback } from "../../../../utils/gammaCalculations";
import type { GammaLevel } from "../types";

const REFRESH_MS = 40_000;
const NEAR_SPOT_PCT = 0.12;

function levelNearSpot(
    levels: GexLevels | undefined,
    key: keyof GexLevels,
    anchor: number,
): number | null {
    if (!levels || !anchor || anchor <= 0) return null;
    const v = levels[key];
    if (v == null || !Number.isFinite(Number(v)) || Number(v) <= 0) return null;
    const n = Number(v);
    if (Math.abs(n - anchor) / anchor > NEAR_SPOT_PCT) return null;
    return n;
}

function mapChainLevels(levels: GexLevels, anchor: number): GammaLevel[] {
    const out: GammaLevel[] = [];
    const specs: {
        key: keyof GexLevels;
        label: string;
        type: GammaLevel["type"];
    }[] = [
        { key: "gammaFlip", label: "GF", type: "gamma_flip" },
        { key: "zeroGamma", label: "ZG", type: "zero_gamma" },
        { key: "callWall", label: "CW", type: "call_wall" },
        { key: "putWall", label: "PW", type: "put_wall" },
    ];
    for (const s of specs) {
        const strike = levelNearSpot(levels, s.key, anchor);
        if (strike != null) {
            out.push({ strike, label: s.label, type: s.type });
        }
    }
    return out;
}

export function useGexChainLevels(symbol: string) {
    const [chainLevels, setChainLevels] = useState<GammaLevel[]>([]);
    const [chainSpot, setChainSpot] = useState(0);
    const [loading, setLoading] = useState(false);
    const timerRef = useRef<ReturnType<typeof setInterval>>();

    const fetch_ = useCallback(async () => {
        if (!symbol) return;
        setLoading(true);
        try {
            const rows: OptionRow[] = await fetchGexChainRows(symbol);
            if (!rows.length) {
                setChainLevels([]);
                return;
            }
            const spot = getUnderlyingPriceFallback(rows, "accumulated");
            if (spot > 0) setChainSpot(spot);
            const groups = calculateGexByDte(rows, ["all"]);
            const allGroup = groups.find((g) => g.dte === "all") || groups[0];
            const lv = allGroup?.levels;
            if (lv && spot > 0) {
                setChainLevels(mapChainLevels(lv, spot));
            }
        } catch {
            /* keep prior */
        } finally {
            setLoading(false);
        }
    }, [symbol]);

    useEffect(() => {
        fetch_();
        timerRef.current = setInterval(fetch_, REFRESH_MS);
        return () => clearInterval(timerRef.current);
    }, [fetch_]);

    return { chainLevels, chainSpot, loading, refetch: fetch_ };
}
